import { supabase } from "@/lib/supabase";

export interface AdminMetrics {
  businessCount: number;
  activeBusinessCount: number;
  userCount: number;
  activeUserCount: number;
  bookingCount: number;
  activeSubscriptionCount: number;
  monthlyRecurringRevenueCents: number;
}

export interface AdminBusinessRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  ownerUserId: string;
  ownerEmail: string;
  isActive: boolean;
  createdAt: string;
  subscriptionPlan: string;
  subscriptionStatus: string;
  subscriptionAmountCents: number;
}

export interface AdminUserRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  businessId: string | null;
  businessName: string;
  isActive: boolean;
  createdAt: string;
}

export interface AdminBookingRow {
  id: string;
  businessId: string;
  businessName: string;
  clientName: string;
  serviceName: string;
  status: string;
  date: string;
  startMinutes: number;
  createdAt: string;
}

export async function fetchAdminMetrics(): Promise<AdminMetrics> {
  const [businesses, users, appointments, subscriptions] = await Promise.all([
    supabase.from("businesses").select("id, is_active"),
    supabase.from("profiles").select("id, is_active"),
    supabase.from("appointments").select("id"),
    supabase.from("subscriptions").select("status, amount_cents, billing_interval"),
  ]);

  const firstError = [businesses.error, users.error, appointments.error, subscriptions.error].find(Boolean);
  if (firstError) {
    throw firstError;
  }

  const subscriptionRows = subscriptions.data ?? [];
  const activeSubscriptions = subscriptionRows.filter((row) => row.status === "active");
  const monthlyRecurringRevenueCents = activeSubscriptions.reduce((total, row) => {
    const amount = row.amount_cents ?? 0;
    return total + (row.billing_interval === "yearly" ? Math.round(amount / 12) : amount);
  }, 0);

  return {
    businessCount: businesses.data?.length ?? 0,
    activeBusinessCount: businesses.data?.filter((row) => row.is_active !== false).length ?? 0,
    userCount: users.data?.length ?? 0,
    activeUserCount: users.data?.filter((row) => row.is_active !== false).length ?? 0,
    bookingCount: appointments.data?.length ?? 0,
    activeSubscriptionCount: activeSubscriptions.length,
    monthlyRecurringRevenueCents,
  };
}

export async function fetchAdminBusinesses(): Promise<AdminBusinessRow[]> {
  const [businesses, profiles, subscriptions] = await Promise.all([
    supabase.from("businesses").select("*").order("created_at", { ascending: false }),
    supabase.from("profiles").select("id, email"),
    supabase.from("subscriptions").select("*"),
  ]);

  const firstError = [businesses.error, profiles.error, subscriptions.error].find(Boolean);
  if (firstError) {
    throw firstError;
  }

  const profileById = new Map((profiles.data ?? []).map((row) => [row.id, row.email]));
  const subscriptionByBusinessId = new Map((subscriptions.data ?? []).map((row) => [row.business_id, row]));

  return (businesses.data ?? []).map((row) => {
    const subscription = subscriptionByBusinessId.get(row.id);
    return {
      id: row.id,
      name: row.name,
      email: row.email ?? "",
      phone: row.phone ?? "",
      address: row.address ?? "",
      ownerUserId: row.owner_user_id,
      ownerEmail: profileById.get(row.owner_user_id) ?? "Unknown owner",
      isActive: row.is_active ?? true,
      createdAt: row.created_at,
      subscriptionPlan: subscription?.plan_name ?? "No plan",
      subscriptionStatus: subscription?.status ?? "unassigned",
      subscriptionAmountCents: subscription?.amount_cents ?? 0,
    };
  });
}

export async function fetchAdminUsers(): Promise<AdminUserRow[]> {
  const [profiles, businesses] = await Promise.all([
    supabase.from("profiles").select("*").order("created_at", { ascending: false }),
    supabase.from("businesses").select("id, name"),
  ]);

  const firstError = [profiles.error, businesses.error].find(Boolean);
  if (firstError) {
    throw firstError;
  }

  const businessById = new Map((businesses.data ?? []).map((row) => [row.id, row.name]));

  return (profiles.data ?? []).map((row) => ({
    id: row.id,
    email: row.email,
    displayName: row.display_name,
    role: row.role,
    businessId: row.business_id ?? null,
    businessName: row.business_id ? businessById.get(row.business_id) ?? "Unknown business" : "Platform",
    isActive: row.is_active ?? true,
    createdAt: row.created_at,
  }));
}

export async function fetchAdminBookings(): Promise<AdminBookingRow[]> {
  const [appointments, businesses, clients, services] = await Promise.all([
    supabase.from("appointments").select("*").order("date", { ascending: false }).order("start_minutes", { ascending: false }),
    supabase.from("businesses").select("id, name"),
    supabase.from("clients").select("id, name"),
    supabase.from("services").select("id, name"),
  ]);

  const firstError = [appointments.error, businesses.error, clients.error, services.error].find(Boolean);
  if (firstError) {
    throw firstError;
  }

  const businessById = new Map((businesses.data ?? []).map((row) => [row.id, row.name]));
  const clientById = new Map((clients.data ?? []).map((row) => [row.id, row.name]));
  const serviceById = new Map((services.data ?? []).map((row) => [row.id, row.name]));

  return (appointments.data ?? []).map((row) => ({
    id: row.id,
    businessId: row.business_id,
    businessName: businessById.get(row.business_id) ?? "Unknown business",
    clientName: clientById.get(row.client_id) ?? "Unknown client",
    serviceName: serviceById.get(row.service_id) ?? "Unknown service",
    status: row.status,
    date: row.date,
    startMinutes: row.start_minutes,
    createdAt: row.created_at,
  }));
}

export async function updateUserActiveState(userId: string, isActive: boolean) {
  const { error } = await supabase.from("profiles").update({ is_active: isActive }).eq("id", userId);
  if (error) {
    throw error;
  }
}

export async function updateBusinessActiveState(businessId: string, isActive: boolean) {
  const { error } = await supabase.from("businesses").update({ is_active: isActive }).eq("id", businessId);
  if (error) {
    throw error;
  }
}
